import styled from '@emotion/styled'
import { createPropFilter } from 'common/emotion'
import { withClass } from 'common/emotion'

import { WebLink, WebLinkProps } from 'common/navigation/web-link'

import { calcRem } from 'component-library/__styling/style-functions'
import { transition, breakWords } from 'component-library/__styling/style-mixins'
import { colors, metrics } from 'component-library/__styling/settings'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'

import {
    TopicLabel,
    Props as SectionAssignmentProps,
} from 'component-library/typography/TopicLabel/TopicLabel'

import { themedValue } from 'component-library/__styling/themed-value'
import { styledKickerTextClassName } from '../Kicker/Kicker.styled'
import { styledCardTimestampTextLabel } from 'component-library/content/CardTimestamp/CardTimestamp.styled'
import { Kicker } from '../Kicker/Kicker'
import { Timestamp, TimestampType } from 'component-library/content/CardTimestamp/CardTimestamp'
import { IconPremium } from 'component-library/icons/IconPremium/IconPremium'

export const styledListCardClassName = 'ListCard'
export const styledLinkClassName = 'ListCard-Link'
export const styledHeadlineClassName = 'ListCard-Headline'

export interface StyledListCardProps {
    simpleList?: boolean
    noStretch?: boolean
}

export const StyledTimestamp = styled(Timestamp)(props => ({
    textTransform: 'uppercase' as 'uppercase',
    marginBottom: themedValue(props.theme, { perthnow: 0, fallback: calcRem(6) }),
}))

export const StyledKicker = styled(Kicker)({
    marginBottom: calcRem(6),
})

export const StyledListCard = withClass(styledListCardClassName)(
    styled('li')<StyledListCardProps>(
        {
            ...breakWords,
            width: '100%',
            listStyle: 'none',
        },
        props => ({
            flexGrow: props.noStretch ? 0 : 1,
            backgroundColor: themedValue(props.theme, {
                perthnow: colors.white,
                fallback: 'transparent',
            }),

            ...conditionalStyles(props.theme.kind === 'perthnow', {
                position: 'relative',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                margin: 0,
            }),

            ...conditionalStyles(props.theme.kind === 'sevennews', {
                ['&:hover']: {
                    [`& .${styledLinkClassName}`]: {
                        color: themedValue(props.theme, {
                            sevennews: section => section.cards.list.hoverColor,
                            fallback: undefined,
                        }),
                    },
                    [`& .${styledKickerTextClassName}`]: {
                        color: themedValue(props.theme, {
                            sevennews: section => section.cards.kicker.hoverColor,
                            fallback: undefined,
                        }),
                    },
                    [`& .${styledCardTimestampTextLabel}`]: {
                        color: themedValue(props.theme, {
                            sevennews: section => section.cards.timestamp.hoverColor,
                            fallback: undefined,
                        }),
                    },
                },
            }),
        })
    )
)

export const StyledHeadline = withClass(styledHeadlineClassName)(
    styled('span')(({ theme }) => ({
        ...conditionalStyles(theme.kind === 'perthnow' || theme.kind === 'thewest', {
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'flex-start',
            flex: '1 1 auto',
            alignSelf: 'stretch',
            overflow: 'hidden',
            color: colors.black,
            fontSize: calcRem(14),
            margin: 0,
        }),
    }))
)

export const StyledTopicLabel = styled(TopicLabel)<SectionAssignmentProps>(props => ({
    ...conditionalStyles(props.theme.kind === 'perthnow', {
        flex: '0 0 auto',
        display: 'flex',
        alignSelf: 'stretch',
    }),
}))

export interface StyledLinkProps {
    simpleList?: boolean
    removeHorizontalMargin?: boolean
    hasSeparators?: boolean
    timestamp?: TimestampType
}

export const StyledLink = withClass(styledLinkClassName)(
    styled(WebLink, {
        shouldForwardProp: createPropFilter<WebLinkProps>()([
            'simpleList',
            'removeHorizontalMargin',
            'hasSeparators',
            'section',
        ]),
    })<StyledLinkProps>(
        {
            textDecoration: 'none',
        },
        props => ({
            lineHeight: themedValue(props.theme, {
                thewest: 1.286,
                fallback: 1.154,
            }),
            display: themedValue(props.theme, {
                perthnow: 'flex',
                fallback: 'block',
            }),
            padding: themedValue(props.theme, {
                thewest: calcRem(metrics.thewest.margins.sm, 0),
                perthnow:
                    props.timestamp !== undefined ? calcRem(21, metrics.perthnow.margins.sm) : 0,
                sevennews: calcRem(8, 0),
            }),
            fontWeight: themedValue(props.theme, {
                perthnow: props.timestamp !== undefined || props.simpleList ? 700 : 400,
                sevennews: 500,
                thewest: 400,
            }),
            margin: props.removeHorizontalMargin
                ? 0
                : themedValue(props.theme, {
                      perthnow: `0 ${calcRem(metrics.perthnow.margins.sm)}`,
                      fallback: undefined,
                  }),
            color: themedValue(props.theme, {
                perthnow: props.timestamp !== undefined ? colors.black : colors.perthnow.greyCoal,
                thewest: colors.black,
                sevennews: section => section.cards.list.primaryColor,
            }),
            fontFamily: themedValue(props.theme, {
                perthnow:
                    props.timestamp !== undefined
                        ? props.theme.fonts.sansSerifCond
                        : props.theme.fonts.sansSerif,
                sevennews: props.theme.fonts.sansSerif,
                fallback: props.theme.fonts.cards.headline,
            }),
            fontSize: themedValue(props.theme, {
                sevennews: calcRem(14),
                thewest: calcRem(14),
                fallback: undefined,
            }),
            borderBottom: props.hasSeparators
                ? themedValue(props.theme, {
                      thewest: `1px solid ${colors.thewest.greyQuokka}`,
                      perthnow: '1px solid black',
                      fallback: 'currentColor',
                  })
                : undefined,

            '&:first-child': {
                borderTop: props.hasSeparators
                    ? themedValue(props.theme, {
                          thewest: `1px solid ${colors.thewest.greyQuokka}`,
                          perthnow: '1px solid black',
                          fallback: 'currentColor',
                      })
                    : undefined,
            },

            '&:last-child': {
                borderBottom: 'none',
            },

            '&:hover': {
                color: themedValue(props.theme, {
                    perthnow: colors.perthnow.blueKyanite,
                    thewest: section => section.primaryColor,
                    fallback: undefined,
                }),
                textDecoration: themedValue(props.theme, {
                    sevennews: 'underline',
                    fallback: undefined,
                }),
            },

            '&:focus': {
                outline: themedValue(props.theme, {
                    thewest: 'none',
                    perthnow: 'none',
                    sevennews: 'solid 2px',
                }),
                textDecoration: themedValue(props.theme, {
                    sevennews: undefined,
                    fallback: 'underline',
                }),
                color: colors.black,
                outlineColor: themedValue(props.theme, {
                    sevennews: section => section.focusColor,
                    fallback: undefined,
                }),
            },

            // to prevent flash on desktop when clicking through
            '&:active': {
                outline: themedValue(props.theme, { sevennews: 'none', fallback: undefined }),
            },

            ...conditionalStyles(props.theme.kind === 'perthnow', {
                flex: '1 1 auto',
                alignSelf: 'center',
                alignItems: 'center',
                height: calcRem(31),
                // Hack to stop long text overflowing container
                width: 0,
                overflow: 'hidden',
                ...transition({ properties: ['color'] }),
            }),
        })
    )
)

export interface StyledLinkTextProps {
    isTruncated?: boolean
}

export const StyledLinkText = styled('span')<StyledLinkTextProps>({}, ({ isTruncated }) => ({
    display: 'block',
    width: '100%',

    ...conditionalStyles(isTruncated, {
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    }),
}))

export interface StyledIconPremiumProps {
    width: number
    height: number
}

export const StyledIconPremium = styled(IconPremium)<StyledIconPremiumProps>(
    ({ width, height }) => ({
        width,
        height,
        verticalAlign: 'text-bottom',
        marginRight: calcRem(1),
    })
)
